import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { LATEST_NEWS_VERSION, NEWS } from "../lib/news";
import { usePersistedState } from "../lib/persistent-state";

/**
 * "What's new" dropdown in the header.
 *
 * A dot appears on the button until the user has opened the panel on the
 * current release. "Seen" is stored per browser in localStorage rather than
 * in /me/settings on purpose: it's a UI convenience, not a preference worth a
 * DB round-trip or a cross-device sync — and a fresh browser showing the notes
 * once more is a harmless outcome.
 */
export function NewsMenu() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [seenVersion, setSeenVersion] = usePersistedState<string>(
    "whosout:news:seenVersion",
    "",
  );
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isFr = i18n.language.startsWith("fr");
  const locale = isFr ? "fr-FR" : "en-GB";
  const hasUnread = LATEST_NEWS_VERSION !== "" && seenVersion !== LATEST_NEWS_VERSION;

  // Close on outside click and on Escape. Escape also returns focus to the
  // button so keyboard users don't get dropped at the top of the document.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    // Opening is what marks the current release as read.
    if (next && hasUnread) setSeenVersion(LATEST_NEWS_VERSION);
  };

  return (
    <div className="news-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="ghost news-trigger"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          hasUnread
            ? t("news.buttonUnread", { version: LATEST_NEWS_VERSION })
            : t("news.button")
        }
        title={t("news.title")}
      >
        <span aria-hidden="true">✨</span>
        <span className="news-trigger-label">{t("news.title")}</span>
        {hasUnread && <span className="news-dot" aria-hidden="true" />}
      </button>

      {open && (
        <div className="news-panel" role="dialog" aria-label={t("news.title")}>
          <div className="news-panel-head">
            <strong>{t("news.title")}</strong>
            <button
              type="button"
              className="ghost news-close"
              onClick={() => {
                setOpen(false);
                buttonRef.current?.focus();
              }}
              aria-label={t("news.close")}
            >
              ✕
            </button>
          </div>

          {NEWS.length === 0 ? (
            <p className="news-empty">{t("news.empty")}</p>
          ) : (
            <ol className="news-list">
              {NEWS.map((entry) => (
                <li key={entry.version}>
                  <div className="news-version">
                    <span className="news-version-tag">v{entry.version}</span>
                    <span className="news-date">
                      {new Date(entry.date).toLocaleDateString(locale, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <ul>
                    {entry.items.map((item, i) => (
                      <li key={i}>{isFr ? item.fr : item.en}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
